import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useLocation } from 'react-router-dom'

const emptyHeader = {
	title: null,
	subtitle: null,
	actions: null,
	children: null,
}

const PageHeaderContext = createContext({
	...emptyHeader,
	setPageHeader: () => {},
	clearPageHeader: () => {},
})

export const usePageHeader = () => useContext(PageHeaderContext)

export const PageHeaderProvider = ({ children }) => {
	const location = useLocation()
	const [header, setHeader] = useState(emptyHeader)
	const previousPathname = useRef(null)

	useEffect(() => {
		// Clear stale header on route change only — not on initial load/reload,
		// otherwise this runs after the page's PageHeader effect and wipes the title.
		if (
			previousPathname.current !== null &&
			previousPathname.current !== location.pathname
		) {
			setHeader(emptyHeader)
		}
		previousPathname.current = location.pathname
	}, [location.pathname])

	const setPageHeader = useCallback((payload) => {
		setHeader({
			title: payload?.title ?? null,
			subtitle: payload?.subtitle ?? null,
			actions: payload?.actions ?? null,
			children: payload?.children ?? null,
		})
	}, [])

	const clearPageHeader = useCallback(() => {
		setHeader(emptyHeader)
	}, [])

	const value = useMemo(
		() => ({
			...header,
			setPageHeader,
			clearPageHeader,
		}),
		[header, setPageHeader, clearPageHeader]
	)

	return (
		<PageHeaderContext.Provider value={value}>
			{children}
		</PageHeaderContext.Provider>
	)
}
